import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Mail, Lock, AlertCircle,
  Atom, Beaker, BookOpen, GraduationCap,
  Microscope, Pencil, Ruler, Lightbulb,
  Globe, Settings, Brain, Calculator,
  Dna, Orbit, Sigma, Box, Binary,
  Layers, Variable, Pi, CircleDashed, Cpu,
  Search, Star, Trophy, FlaskConical, Thermometer,
  Database, Code, Terminal, Infinity, Zap,
  Puzzle, MousePointer2, ClipboardList
} from 'lucide-react';

const SignIn = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Stable random icons using a jittered grid to prevent excessive overlapping
  const backgroundIcons = React.useMemo(() => {
    const iconTypes = [
      Atom, Beaker, BookOpen, GraduationCap, Microscope, Pencil, Ruler, Lightbulb,
      Globe, Settings, Brain, Calculator, Dna, Orbit, Sigma, Box, Binary,
      Layers, Variable, Pi, CircleDashed, Cpu, Search, Star, Trophy,
      FlaskConical, Thermometer, Database, Code, Terminal, Infinity, Zap,
      Puzzle, MousePointer2, ClipboardList
    ];

    const rows = 16;
    const cols = 16;
    const items = [];

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const index = r * cols + c;
        items.push({
          Icon: iconTypes[index % iconTypes.length],
          top: `${(r / rows) * 100 + (Math.random() * 4 - 2)}%`,
          left: `${(c / cols) * 100 + (Math.random() * 4 - 2)}%`,
          rotate: `${(Math.random() - 0.5) * 40}deg`,
          scale: 0.8 + Math.random() * 0.5,
          opacity: 0.1 + Math.random() * 0.15
        });
      }
    }
    return items;
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);


    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Failed to sign in. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0d47a1] via-[#1565c0] to-[#0d47a1] relative overflow-hidden py-12 px-4 sm:px-6 lg:px-8">
      {/* Randomized Educational Icon Pattern Background - Ultra Dense & Highly Visible */}
      <div className="absolute inset-0 pointer-events-none select-none">
        {backgroundIcons.map((item, i) => (
          <div
            key={i}
            className={`absolute pointer-events-auto cursor-default group 
              ${i % 3 === 0 ? 'hidden sm:block' : ''} 
              ${i % 5 === 0 ? 'hidden md:block' : ''} 
              ${i % 7 === 0 ? 'hidden lg:block' : ''}`}
            style={{
              top: item.top,
              left: item.left,
              transform: `rotate(${item.rotate}) scale(${item.scale})`,
              opacity: item.opacity,
              zIndex: 0
            }}
          >
            <div className="transition-all duration-500 ease-out group-hover:scale-[1.5] group-hover:rotate-[15deg] group-hover:opacity-100 text-white">
              <item.Icon className="w-5 h-5 sm:w-10 sm:h-10 md:w-12 md:h-12 drop-shadow-[0_0_8px_rgba(255,255,255,0.2)] group-hover:drop-shadow-[0_0_15px_rgba(255,255,255,0.5)]" strokeWidth={1.5} />
            </div>
          </div>
        ))}
      </div>

      {/* Radiant Glows to match the "Light" areas in the reference */}
      <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] bg-blue-400/20 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-10%] left-[-5%] w-[600px] h-[600px] bg-indigo-400/20 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-md w-full space-y-8 relative z-10">
        <div className="text-center">
          <div className="flex justify-center items-center space-x-3 mb-8">
            <img
              src="/logo/nep-logo.png"
              alt="NEP"
              className="h-16 w-auto drop-shadow-[0_0_20px_rgba(255,255,255,0.4)]"
            />
            <div className="flex flex-col text-left border-l-2 border-white/20 pl-4">
              <span className="text-4xl font-bold text-white tracking-tight">NEP</span>
              <span className="text-xs font-semibold text-blue-200 tracking-wider">NextGen Exam Platform</span>
            </div>
          </div>
        </div>

        {/* Glass Card */}
        <div className="bg-white/[0.08] backdrop-blur-3xl border border-white/20 rounded-3xl p-10 shadow-[0_20px_50px_rgba(0,0,0,0.3)]">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-white">Welcome Back</h2>
              <p className="text-blue-100 text-sm mt-2 font-medium">Please sign in to your account</p>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 flex items-center space-x-2">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <span className="text-sm text-red-200">{error}</span>
              </div>
            )}

            <div className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1.5 ml-1">
                  Email address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-blue-400/70" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/60 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
                    placeholder="Email ID"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5 ml-1">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-300">
                    Password
                  </label>
                  <Link to="/forgot-password" title="Forgot Password" className="text-sm font-medium text-blue-400 hover:text-blue-300 transition-colors">
                    Reset password?
                  </Link>
                </div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-blue-400/70" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/60 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none font-sans"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 px-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white rounded-xl font-semibold shadow-lg shadow-blue-900/20 transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/30 border-t-white"></div>
                  <span>Signing in...</span>
                </>
              ) : (
                <span>Sign in</span>
              )}
            </button>

            <div className="text-center space-y-4 pt-2">
              <p className="text-sm text-gray-400">
                Don't have an account?{' '}
                <Link to="/signup" className="font-semibold text-blue-400 hover:text-blue-300 transition-colors">
                  Sign up
                </Link>
              </p>
              <Link to="/" className="inline-block text-sm font-medium text-white/60 hover:text-white transition-colors">
                ← Back to Home
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
